import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { useSnackbar } from 'notistack';
import { ApiKeyStatus } from '../../types';
import { createApiKey, deleteApiKey, getApiKeyStatus } from '../../services/users';

interface ApiKeyManagerProps {
  lang: string;
}

type ConfirmAction = 'regenerate' | 'revoke' | null;

function ApiKeyManager({ lang }: ApiKeyManagerProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [status, setStatus] = useState<ApiKeyStatus | null>(null);
  const [newApiKey, setNewApiKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
  const isEnglish = lang === 'en';

  useEffect(() => {
    let active = true;

    const loadStatus = async () => {
      try {
        const data = await getApiKeyStatus();
        if (active) setStatus(data);
      } catch (error) {
        console.error('Error loading API key status:', error);
        if (active) enqueueSnackbar(isEnglish ? 'Failed to load API key status' : '加载 API Key 状态失败', { variant: 'error' });
      } finally {
        if (active) setLoading(false);
      }
    };

    loadStatus();
    return () => {
      active = false;
    };
  }, [enqueueSnackbar, isEnglish]);

  const formatDate = (value?: string) => {
    if (!value) return isEnglish ? 'Never' : '从未使用';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat(isEnglish ? 'en' : 'zh-CN', { dateStyle: 'medium', timeStyle: 'short' }).format(date);
  };

  const handleCreate = async () => {
    setProcessing(true);
    try {
      const created = await createApiKey();
      setNewApiKey(created.apiKey);
      setStatus({ hasKey: true, prefix: created.prefix, createdAt: created.createdAt });
      enqueueSnackbar(isEnglish ? 'API key created' : 'API Key 已创建', { variant: 'success' });
    } catch (error) {
      console.error('Error creating API key:', error);
      enqueueSnackbar(isEnglish ? 'Failed to create API key' : '创建 API Key 失败', { variant: 'error' });
    } finally {
      setProcessing(false);
      setConfirmAction(null);
    }
  };

  const handleRevoke = async () => {
    setProcessing(true);
    try {
      await deleteApiKey();
      setNewApiKey('');
      setStatus({ hasKey: false });
      enqueueSnackbar(isEnglish ? 'API key revoked' : 'API Key 已撤销', { variant: 'success' });
    } catch (error) {
      console.error('Error revoking API key:', error);
      enqueueSnackbar(isEnglish ? 'Failed to revoke API key' : '撤销 API Key 失败', { variant: 'error' });
    } finally {
      setProcessing(false);
      setConfirmAction(null);
    }
  };

  const handleConfirm = () => {
    if (confirmAction === 'regenerate') {
      handleCreate();
      return;
    }
    if (confirmAction === 'revoke') {
      handleRevoke();
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(newApiKey);
      enqueueSnackbar(isEnglish ? 'API key copied' : 'API Key 已复制', { variant: 'success' });
    } catch (error) {
      console.error('Error copying API key:', error);
      enqueueSnackbar(isEnglish ? 'Failed to copy API key' : '复制 API Key 失败', { variant: 'error' });
    }
  };

  const confirmTitle = confirmAction === 'regenerate'
    ? isEnglish ? 'Regenerate API key?' : '重新生成 API Key？'
    : isEnglish ? 'Revoke API key?' : '撤销 API Key？';
  const confirmBody = confirmAction === 'regenerate'
    ? isEnglish ? 'The current key will stop working immediately. Update any integrations after copying the new key.' : '当前 Key 将立即失效。请复制新的 Key 后更新所有集成。'
    : isEnglish ? 'This key will stop working immediately. This action cannot be undone.' : '此 Key 将立即失效，且操作无法撤销。';

  return (
    <Paper variant="outlined" sx={{ mt: 2, p: 2.5 }}>
      <Stack spacing={2}>
        <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={{ display: 'grid', placeItems: 'center', width: 40, height: 40, borderRadius: 2, color: 'primary.main', bgcolor: 'rgba(181,72,37,0.12)' }}>
              <VpnKeyIcon />
            </Box>
            <Box>
              <Typography variant="h6">{isEnglish ? 'API Key' : 'API Key'}</Typography>
              <Typography variant="body2" color="text.secondary">
                {isEnglish ? 'Use an API key for trusted server-to-server requests.' : '使用 API Key 进行可信的服务端或 Agent 调用。'}
              </Typography>
            </Box>
          </Stack>
          {loading ? <CircularProgress size={22} /> : <Chip label={status?.hasKey ? isEnglish ? 'Active' : '已启用' : isEnglish ? 'Not created' : '未创建'} color={status?.hasKey ? 'success' : 'default'} size="small" />}
        </Stack>

        <Divider />

        {newApiKey && (
          <Alert severity="warning" action={<Tooltip title={isEnglish ? 'Copy API key' : '复制 API Key'}><IconButton color="inherit" onClick={handleCopy}><ContentCopyIcon /></IconButton></Tooltip>}>
            <Typography variant="subtitle2" gutterBottom>{isEnglish ? 'Copy this key now. It will not be shown again.' : '请立即复制，此 Key 不会再次显示。'}</Typography>
            <Typography component="code" sx={{ display: 'block', fontFamily: 'monospace', overflowWrap: 'anywhere' }}>{newApiKey}</Typography>
          </Alert>
        )}

        {!loading && (
          <Stack spacing={1.25}>
            <Box>
              <Typography variant="body2" color="text.secondary">{isEnglish ? 'Visible prefix' : '可见前缀'}</Typography>
              <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>{status?.prefix || (isEnglish ? 'No API key' : '暂无 API Key')}</Typography>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" color="text.secondary">{isEnglish ? 'Created' : '创建时间'}</Typography>
                <Typography variant="body1">{status?.hasKey ? formatDate(status.createdAt) : '-'}</Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" color="text.secondary">{isEnglish ? 'Last used' : '最近使用'}</Typography>
                <Typography variant="body1">{status?.hasKey ? formatDate(status.lastUsedAt) : '-'}</Typography>
              </Box>
            </Stack>
          </Stack>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
          {!status?.hasKey ? (
            <Button variant="contained" startIcon={processing ? <CircularProgress size={18} /> : <VpnKeyIcon />} disabled={loading || processing} onClick={handleCreate}>
              {isEnglish ? 'Create API Key' : '创建 API Key'}
            </Button>
          ) : (
            <>
              <Button variant="outlined" startIcon={<AutorenewIcon />} disabled={processing} onClick={() => setConfirmAction('regenerate')}>
                {isEnglish ? 'Regenerate' : '重新生成'}
              </Button>
              <Button variant="outlined" color="error" startIcon={<DeleteOutlineIcon />} disabled={processing} onClick={() => setConfirmAction('revoke')}>
                {isEnglish ? 'Revoke' : '撤销'}
              </Button>
            </>
          )}
        </Stack>
      </Stack>

      <Dialog open={confirmAction !== null} onClose={() => setConfirmAction(null)}>
        <DialogTitle>{confirmTitle}</DialogTitle>
        <DialogContent>
          <Typography>{confirmBody}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAction(null)} disabled={processing}>{isEnglish ? 'Cancel' : '取消'}</Button>
          <Button variant="contained" color={confirmAction === 'revoke' ? 'error' : 'primary'} disabled={processing} onClick={handleConfirm}>
            {processing ? <CircularProgress size={18} /> : isEnglish ? 'Confirm' : '确认'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

export default ApiKeyManager;
